import React, { useState, useEffect } from 'react';
import { FaSignOutAlt, FaBars, FaTimes, FaUserCog, FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import './Dashboard.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const USERS_PER_PAGE = 10;

const Dashboard = () => {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);

  // User management state
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "user" });

  // Fetch users from API
  useEffect(() => {
    if (showManageUsers) {
      fetchUsers();
    }
    // eslint-disable-next-line
  }, [showManageUsers]);

  const fetchUsers = async () => {
    try {
      const response = await fetch('https://elitewebapi.onrender.com/api/Users');
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      alert('Failed to fetch users');
    }
  };

  const programs = {
    'Prime Programme': ['Class I', 'Class II', 'Class III', 'Class IV', 'Class V'],
    'Techno Unique': ['Class VI', 'Class VII', 'Class VIII', 'Class IX', 'Class X'],
    'Olympiad Spectra': ['Class VI', 'Class VII', 'Class VIII', 'Class IX']
  };

  const handleProgramClick = (program) => {
    setSelectedProgram(program === selectedProgram ? null : program);
    setSelectedClass(null);
    setShowManageUsers(false);
  };

  const handleClassClick = (cls) => {
    setSelectedClass(cls);
    setShowManageUsers(false);
  };

  const handleLogout = () => {
    window.location.href = '/';
  };

  // User management handlers
  const handleManageUsersClick = () => {
    setShowManageUsers(true);
    setSelectedProgram(null);
    setSelectedClass(null);
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password || !newUser.role) return;
    try {
      const response = await fetch('https://elitewebapi.onrender.com/api/Users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser),
      });
      if (response.ok) {
        fetchUsers();
        setNewUser({ name: "", email: "", password: "", role: "user" });
      } else {
        alert('Failed to add user');
      }
    } catch {
      alert('Failed to add user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user, password: "" }); // Password is not returned from API, so leave blank
  };

  const handleUpdateUser = async () => {
    try {
      const response = await fetch(`https://elitewebapi.onrender.com/api/Users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingUser),
      });
      if (response.ok) {
        fetchUsers();
        setEditingUser(null);
      } else {
        alert('Failed to update user');
      }
    } catch {
      alert('Failed to update user');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await fetch(`https://elitewebapi.onrender.com/api/Users/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchUsers();
      } else {
        alert('Failed to delete user');
      }
    } catch {
      alert('Failed to delete user');
    }
  };

  // Pagination
  const totalPages = Math.ceil(users.length / USERS_PER_PAGE);
  const paginatedUsers = users.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <header className="header-container">
        <div className="logo">Admin Dashboard ✅</div>
        <nav className={`nav-links ${isMenuOpen ? 'nav-active' : ''}`}>
          <a href="#home">Home</a>
          <a href="#about">About</a>
          <a href="#programmes">Programmes</a>
          <a href="#contact">Contact</a>
          <button className="logout-button-header" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </nav>
        <div className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </div>
      </header>

      {/* Main Body Container */}
      <div className="body-container">
        {/* Sidebar */}
        <div className="sidebar">
          <button
            className={`program-button ${showManageUsers ? 'active' : ''}`}
            onClick={handleManageUsersClick}
          >
            <FaUserCog style={{ marginRight: 8 }} />
            Manage Users
          </button>
          {Object.keys(programs).map((program) => (
            <div key={program}>
              <button
                className="program-button"
                onClick={() => handleProgramClick(program)}
              >
                {program} {selectedProgram === program ? '▼' : '▶'}
              </button>
              {selectedProgram === program && (
                <div className="class-list">
                  {programs[program].map((cls) => (
                    <button
                      key={cls}
                      className="class-button"
                      onClick={() => handleClassClick(cls)}
                    >
                      {cls}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="main-content">
          {showManageUsers ? (
            <div className="user-management">
              <h2>
                <FaUserCog style={{ marginRight: 8 }} />
                Manage Users
              </h2>
              {/* Add User Form */}
              <div className="user-form">
                <input
                  type="text"
                  placeholder="Name"
                  value={newUser.name}
                  onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Password"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                />
                <select
                  value={newUser.role}
                  onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="Staff">Staff</option>
                  <option value="Admin">Admin</option>
                  <option value="Student">Student</option>
                </select>
                <button className="add-user-btn" onClick={handleAddUser}>
                  <FaPlus /> Add User
                </button>
              </div>
              {/* User Table */}
              <div className="user-table-container">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Role</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map(user => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>
                          {editingUser && editingUser.id === user.id ? (
                            <input
                              value={editingUser.name}
                              onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                            />
                          ) : (
                            user.name
                          )}
                        </td>
                        <td>
                          {editingUser && editingUser.id === user.id ? (
                            <input
                              value={editingUser.email}
                              onChange={e => setEditingUser({ ...editingUser, email: e.target.value })}
                            />
                          ) : (
                            user.email
                          )}
                        </td>
                        <td>
                          {editingUser && editingUser.id === user.id ? (
                            <input
                              value={editingUser.password}
                              onChange={e => setEditingUser({ ...editingUser, password: e.target.value })}
                            />
                          ) : (
                            "******"
                          )}
                        </td>
                        <td>
                          {editingUser && editingUser.id === user.id ? (
                            <select
                              value={editingUser.role}
                              onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            user.role
                          )}
                        </td>
                        <td>
                          {editingUser && editingUser.id === user.id ? (
                            <>
                              <button className="save-btn" onClick={handleUpdateUser}>Save</button>
                              <button className="cancel-btn" onClick={() => setEditingUser(null)}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="edit-btn" onClick={() => handleEditUser(user)}>
                                <FaEdit />
                              </button>
                              <button className="delete-btn" onClick={() => handleDeleteUser(user.id)}>
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination */}
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : selectedClass ? (
            <>
              <h2 className="header-title">{selectedProgram} ➝ {selectedClass}</h2>
              <div className="content-buttons">
                <button className="button button1">📅 Micro Schedule : 1</button>
                <button className="button button3">📝 Examinations : 1</button>
                <button className="button button4">📊 Analysis : 0</button>
                <button className="button button5">🔍 Key & Solutions : 0</button>
              </div>
            </>
          ) : (
            <div className="welcome-message">
              <h2>Welcome to the Admin Dashboard</h2>
              <p>Select a program/class or Manage Users from the sidebar.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;